import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ScrollArea } from '@deepguide-ai/ui/scroll-area';
import { Button } from '@deepguide-ai/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@deepguide-ai/ui/dialog';
import { queryClient, trpc } from '@/lib/trpc';
import { vendorLogoPath } from '@/lib/vendor-logo';
import type { ConfigModalProps, IntegrationProvider } from '../types';
import { openOAuthConnectPopup } from '../oauth-connect-lifecycle';

function DropboxConfigModal({ open, onOpenChange, integration, workspaceId, onComplete }: ConfigModalProps) {
  const [connecting, setConnecting] = useState(false);
  const integrationId = integration?.id ?? '';
  const status = useQuery({ ...trpc.integrations.dropbox.status.queryOptions({ integrationId }), enabled: open && !!integrationId });
  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className='sm:max-w-xl'>
      <DialogHeader>
        <DialogTitle>Dropbox evidence</DialogTitle>
        <DialogDescription>Authorize read access to Dropbox Business team identities and groups.</DialogDescription>
      </DialogHeader>
      <ScrollArea type='scroll' className='max-h-[65vh]' viewClassName='max-h-[65vh] [&>div]:!block'>
      <div className='space-y-3 px-6 pb-4 text-sm'>
        {integrationId && status.isLoading ? <p role='status'>Loading Dropbox connection…</p> : null}
        {status.error ? <div className='space-y-2'>
          <p role='alert' className='text-destructive'>{status.error.message}</p>
          <Button variant='outline' disabled={status.isFetching} onClick={() => void status.refetch()}>Retry loading</Button>
        </div> : status.data ? <dl className='space-y-1'><div><dt className='text-muted-foreground'>Connected account</dt><dd>{status.data.providerAccount}</dd></div></dl> : null}
        <p className='text-muted-foreground'>Connecting alone does not collect evidence. Use Scan on the Dropbox card to collect and inspect evidence, and Access reviews to review collected accounts. Empty or incomplete collections retain the previous review population. Effective privilege, MFA and SSO remain unknown.</p>
        <p className='text-muted-foreground'>Audit collection covers selected login, membership, group, app and policy categories for the past 30 days. Historical gaps, missing actors and late events remain possible; this does not prove retention, alerts or human review.</p>
        <p className='text-muted-foreground'>Sharing policy evidence includes team folder and link settings. Password and expiry defaults apply to new external links; these settings do not prove protection of existing files or links.</p>
        <p className='text-muted-foreground'>Consent grants read access to team information, members, groups and security/admin activity. Reconnect existing connections after the operator enables the auditing permission. File contents and recordings are not requested.</p>
        <p className='text-muted-foreground'>A platform operator must configure the Dropbox OAuth app before connecting. Authorize with a Dropbox Business team admin. Disconnect before switching teams or authorizing admins.</p>
        {status.data?.lastError ? <p role='alert' className='text-destructive'>{status.data.lastError}</p> : null}
        <p className='text-muted-foreground'>Disconnect from the integration card to stop future collection. Collected evidence is retained. Revoke app access in Dropbox separately.</p>
      </div>
      </ScrollArea>
      <DialogFooter>
        <Button disabled={connecting} onClick={async () => {
          setConnecting(true);
          try { await DropboxProvider.handleConnect({ workspaceId, onSuccess: onComplete, onFinish: () => setConnecting(false) }); }
          catch { setConnecting(false); }
        }}>{connecting ? 'Connecting…' : integrationId ? 'Reconnect Dropbox' : 'Connect Dropbox'}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}

export const DropboxProvider: IntegrationProvider = {
  id: 'Dropbox', name: 'Dropbox', description: 'Dropbox Business team access and sharing policies',
  Icon: () => <img src={vendorLogoPath('dropbox')} alt='' className='size-6 object-contain' />,
  ConfigModal: DropboxConfigModal, postAuthAction: 'CONFIGURE',
  getFeatures: () => [
    { id: 'access', name: 'Team access inventory', description: 'Read members, reported roles and direct groups', enabled: true },
    { id: 'audit', name: 'Security/admin activity', description: 'Selected categories from the past 30 days; coverage and monitoring require review', enabled: true },
    { id: 'sharing', name: 'Sharing policies', description: 'Team link and folder settings; effective content exposure needs review', enabled: true },
  ],
  handleConnect: async ({ onSuccess, onFinish }) => {
    const result = await queryClient.getMutationCache().build(queryClient, trpc.integrations.dropbox.initAuth.mutationOptions()).execute(undefined).catch(error => {
      toast.error(error instanceof Error ? error.message : 'Could not start Dropbox authorization');
      onFinish?.('error');
      throw error;
    });
    openOAuthConnectPopup({ authUrl: result.authUrl, windowName: 'dropbox-auth', provider: 'Dropbox', onSuccess, onFinish,
      onError: message => toast.error(message), onPopupBlocked: () => toast.error('Allow popups to connect Dropbox.'),
    });
  },
  onConnectComplete: async ({ code, state }) => {
    const result = await queryClient.getMutationCache().build(queryClient, trpc.integrations.dropbox.completeAuth.mutationOptions()).execute({ code, state });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.integrations.dropbox.status.queryKey() }),
    ]);
    return result;
  },
  syncData: async () => { throw new Error('Use Scan on the Dropbox integration card to collect access evidence.'); },
};
